interface Props {
  /** Data rows the file was parsed into. Zero means there is no denominator yet. */
  total: number
  completed: number
  failed: number
  className?: string
}

/**
 * A file import's rows against the total the file was parsed into.
 *
 * Renders nothing without a total. An import that finished before ORB-648 stamped one, or whose
 * file has not been parsed yet, has no denominator — and a bar drawn against rows created so far
 * is the exact illusion this exists to remove: it fills to 100% while thousands of rows were
 * never created at all.
 *
 * Two segments rather than one, matching the Completed and Failed counts beneath it, so the
 * remaining track is the pending figure. Deliberately no percentage: the success rate this
 * replaced divided a count that was always the file total by one that was always zero and read
 * 100% however many rows failed, and a number that is silently wrong is worse than no number.
 *
 * Not built on LeadGoalBar, which is a single-segment goal bar that turns gold on reaching 100% —
 * an import where every row failed would celebrate.
 */
export function FileImportProgressBar({ total, completed, failed, className = '' }: Props) {
  if (total <= 0) return null

  // Clamped because these three counters are written from more than one place. A stale one should
  // leave the bar short, never overflow it into the segment beside it.
  const settledCompleted = Math.min(total, Math.max(0, completed))
  const settledFailed = Math.min(total - settledCompleted, Math.max(0, failed))
  const completedWidth = (settledCompleted / total) * 100
  const failedWidth = (settledFailed / total) * 100

  return (
    <div className={className}>
      <div className="text-xs text-slate-500 dark:text-slate-400 mb-1.5">
        {total.toLocaleString()} {total === 1 ? 'row' : 'rows'} in file
      </div>
      <div
        className="w-full h-2 rounded-full bg-slate-200 dark:bg-slate-700 overflow-hidden flex"
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={settledCompleted + settledFailed}
        aria-label={`${settledCompleted.toLocaleString()} completed and ${settledFailed.toLocaleString()} failed of ${total.toLocaleString()} rows`}
      >
        <div className="h-full bg-green-500 dark:bg-green-400" style={{ width: `${completedWidth}%` }} />
        <div className="h-full bg-red-500 dark:bg-red-400" style={{ width: `${failedWidth}%` }} />
      </div>
    </div>
  )
}
